import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import {
  changeMemberRole,
  removeMember,
  resendMemberInvite,
  resetMemberPassword,
  setMemberCallLimit,
} from "@/api/projects";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { usePermissions } from "@/hooks/usePermissions";
import { useGrantablePermissions } from "@/hooks/useGrantablePermissions";
import { useAuthStore } from "@/stores/auth.store";
import type {
  MemberPasswordResetResponse,
  ProjectMember,
  Role,
} from "@/api/types";

interface MemberDetailSheetProps {
  member: ProjectMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectSlug: string;
  roles: Role[];
  /** Bubbles up the new role data so the parent list re-renders. */
  onMemberUpdated: (member: ProjectMember) => void;
  /** Bubbles up the removed member's id so the parent drops the row. */
  onMemberRemoved: (memberId: string) => void;
}

export function MemberDetailSheet({
  member,
  open,
  onOpenChange,
  projectSlug,
  roles,
  onMemberUpdated,
  onMemberRemoved,
}: MemberDetailSheetProps) {
  const perms = usePermissions();
  const grantable = useGrantablePermissions();
  const currentUser = useAuthStore((s) => s.user);

  const canManage = perms.canManageMembers;
  const isSelf = !!member && currentUser?.id === member.id;
  const isInvitePending = member?.is_active === false;
  const isSuperadmin = !!member?.is_superadmin;

  const [savingRole, setSavingRole] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] =
    useState<MemberPasswordResetResponse | null>(null);
  const [resending, setResending] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Reset transient state when the sheet closes or the member changes.
  useEffect(() => {
    setConfirmReset(false);
    setResetResult(null);
    setConfirmRemove(false);
    setEmailCopied(false);
  }, [member?.id, open]);

  if (!member) return null;

  async function changeRole(roleId: number) {
    if (!member || roleId === member.role.id) return;
    setSavingRole(true);
    try {
      const updated = await changeMemberRole(projectSlug, member.id, roleId);
      onMemberUpdated(updated);
      toast.success(`${updated.full_name} is now ${updated.role.name}`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSavingRole(false);
    }
  }

  async function handleResetConfirmed() {
    if (!member) return;
    setResetting(true);
    try {
      const res = await resetMemberPassword(projectSlug, member.id);
      setResetResult(res);
      setConfirmReset(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setResetting(false);
    }
  }

  async function handleResend() {
    if (!member) return;
    setResending(true);
    try {
      await resendMemberInvite(projectSlug, member.email);
      toast.success(`Invitation resent to ${member.email}`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setResending(false);
    }
  }

  async function handleRemoveConfirmed() {
    if (!member) return;
    setRemoving(true);
    try {
      await removeMember(projectSlug, member.id);
      toast.success(`${member.full_name} removed from the project`);
      onMemberRemoved(member.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setRemoving(false);
    }
  }

  async function copyEmail() {
    if (!member) return;
    try {
      await navigator.clipboard.writeText(member.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Select manually instead.");
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[460px]">
        <SheetTitle className="sr-only">Member details</SheetTitle>
        <SheetDescription className="sr-only">
          Inspect a project member, change their role, reset their password,
          or remove them from the project.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start gap-4 border-b border-slate-900/[0.06] px-6 pb-5 pt-6 pr-12">
          <Avatar className="h-14 w-14 border border-slate-200">
            <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-base font-semibold text-white">
              {initials(member.full_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-xl font-semibold tracking-tight text-slate-900">
                {member.full_name}
              </h2>
              {isSelf && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  You
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => void copyEmail()}
              className="mt-1 inline-flex max-w-full items-center gap-1.5 truncate text-sm text-indigo-600 hover:text-indigo-500"
            >
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{member.email}</span>
              {emailCopied ? (
                <Check className="h-3 w-3 shrink-0 text-emerald-500" />
              ) : (
                <Copy className="h-3 w-3 shrink-0 opacity-60" />
              )}
            </button>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <RoleBadge roleName={member.role.name} />
              {isInvitePending && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                  <Mail className="h-2.5 w-2.5" />
                  Invitation pending
                </span>
              )}
              {isSuperadmin && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  Superadmin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* Role */}
          <section>
            <SectionHeader>Role</SectionHeader>
            <Select
              value={String(member.role.id)}
              onValueChange={(v) => {
                const id = Number(v);
                if (Number.isFinite(id)) void changeRole(id);
              }}
              disabled={!canManage || savingRole || isSelf}
            >
              <SelectTrigger className="h-10 rounded-lg border-slate-200 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => {
                  const missing = grantable.unsatisfiable(r.permissions);
                  const escalated = missing.length > 0;
                  const tooltip = escalated
                    ? `This role has permissions you don't have (${missing.join(", ")}). Only a superadmin can assign it.`
                    : undefined;
                  return (
                    <SelectItem
                      key={r.id}
                      value={String(r.id)}
                      disabled={escalated}
                      title={tooltip}
                      className={
                        escalated
                          ? "cursor-not-allowed opacity-60"
                          : undefined
                      }
                    >
                      <span className="flex items-center gap-2">
                        <span>{r.name}</span>
                        {r.is_system && (
                          <span className="text-[9px] uppercase tracking-wider text-slate-400">
                            System
                          </span>
                        )}
                        {escalated && (
                          <Lock className="h-3 w-3 text-amber-600" />
                        )}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {isSelf && (
              <p className="mt-1.5 text-xs text-slate-400">
                You can&rsquo;t change your own role.
              </p>
            )}
          </section>

          {/* Call limit — a salesperson on trial; superadmins are never limited. */}
          {!isSuperadmin && (
            <CallLimitSection
              member={member}
              projectSlug={projectSlug}
              canManage={canManage}
              onMemberUpdated={onMemberUpdated}
            />
          )}

          {/* Account */}
          <section>
            <SectionHeader>Account</SectionHeader>
            <dl className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm">
              <DLRow
                label="Status"
                value={
                  isInvitePending ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Invitation pending
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Active
                    </span>
                  )
                }
              />
              <DLRow label="Last login" value="—" muted />
            </dl>
          </section>

          {/* Actions */}
          {canManage && !isSelf && (
            <section>
              <SectionHeader>Actions</SectionHeader>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start border-amber-200 text-amber-800 hover:bg-amber-50"
                  onClick={() => setConfirmReset(true)}
                >
                  <KeyRound className="mr-2 h-3.5 w-3.5" />
                  Reset password
                </Button>
                {isInvitePending && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    onClick={() => void handleResend()}
                    disabled={resending}
                  >
                    {resending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                    )}
                    Resend invitation
                  </Button>
                )}
                {!isSuperadmin && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start border-rose-200 text-rose-700 hover:bg-rose-50"
                    onClick={() => setConfirmRemove(true)}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Remove from project
                  </Button>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-slate-900/[0.06] px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>

        {/* Reset confirmation */}
        <Dialog
          open={confirmReset}
          onOpenChange={(o) => {
            if (resetting) return;
            if (!o) setConfirmReset(false);
          }}
        >
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <KeyRound className="h-5 w-5" />
              </div>
              <DialogTitle className="text-center text-lg">
                Reset password for {member.full_name}?
              </DialogTitle>
              <DialogDescription className="text-center text-sm leading-relaxed">
                A new password will be generated. The current password will
                stop working immediately.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmReset(false)}
                disabled={resetting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleResetConfirmed()}
                disabled={resetting}
                className="border-amber-300 bg-amber-600 text-white hover:bg-amber-700"
              >
                {resetting && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Reset password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset result */}
        <Dialog
          open={!!resetResult}
          onOpenChange={(o) => {
            if (!o) setResetResult(null);
          }}
        >
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>
                New password for {resetResult?.full_name}
              </DialogTitle>
              <DialogDescription>
                Share these credentials securely. They won&rsquo;t be shown
                again.
              </DialogDescription>
            </DialogHeader>
            {resetResult && (
              <ResetResultCard
                email={resetResult.email}
                password={resetResult.generated_password}
              />
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="primary"
                onClick={() => setResetResult(null)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove confirmation */}
        <Dialog
          open={confirmRemove}
          onOpenChange={(o) => {
            if (removing) return;
            if (!o) setConfirmRemove(false);
          }}
        >
          <DialogContent className="sm:max-w-[440px]">
            <DialogHeader>
              <DialogTitle>Remove member</DialogTitle>
              <DialogDescription>
                Remove{" "}
                <span className="font-medium text-slate-900">
                  {member.full_name}
                </span>{" "}
                from this project? They will lose access immediately.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmRemove(false)}
                disabled={removing}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void handleRemoveConfirmed()}
                disabled={removing}
              >
                {removing && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/** How many numbers the Call Center may ever hand this member (owner,
 *  2026-10-03: a salesperson on trial gets e.g. 200, the current team none).
 *  Off by default; turning it off removes the limit at once. */
function CallLimitSection({
  member,
  projectSlug,
  canManage,
  onMemberUpdated,
}: {
  member: ProjectMember;
  projectSlug: string;
  canManage: boolean;
  onMemberUpdated: (member: ProjectMember) => void;
}) {
  const current = member.call_limit ?? null;
  const [limited, setLimited] = useState(current !== null);
  const [value, setValue] = useState(String(current ?? 200));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLimited(current !== null);
    setValue(String(current ?? 200));
  }, [member.id, current]);

  const parsed = Number(value);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 100000;
  const used = member.call_limit_used ?? 0;

  async function save(next: number | null) {
    setSaving(true);
    try {
      const res = await setMemberCallLimit(projectSlug, member.id, next);
      onMemberUpdated({
        ...member,
        call_limit: res.limit,
        call_limit_used: res.limit === null ? null : res.used,
      });
      toast.success(
        next === null
          ? `${member.full_name} has no call limit`
          : `${member.full_name} is limited to ${next} numbers`,
      );
    } catch (err) {
      toast.error(extractMessage(err));
      setLimited(current !== null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <SectionHeader>Call limit</SectionHeader>
      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-900">Limit the numbers</div>
            <p className="text-xs text-slate-500">
              For a salesperson on trial: the Call Center stops handing new leads at the
              limit. Calling back their own leads stays possible.
            </p>
          </div>
          <Switch
            checked={limited}
            disabled={!canManage || saving}
            aria-label="Limit the numbers"
            onCheckedChange={(on) => {
              setLimited(on);
              if (!on && current !== null) void save(null);
            }}
          />
        </div>
        {limited && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={100000}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!canManage || saving}
              aria-label="Numbers allowed"
              className="h-9 w-28 rounded-lg border-slate-200"
            />
            <span className="text-sm text-slate-500">numbers</span>
            <Button
              type="button"
              size="sm"
              className="ml-auto"
              disabled={!canManage || saving || !valid || parsed === current}
              onClick={() => void save(parsed)}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        )}
        {current !== null && (
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-500">
              <span>Handed out</span>
              <span className="tabular-nums">
                {used} / {current}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className={cn(
                  "h-full rounded-full",
                  used >= current ? "bg-rose-500" : "bg-indigo-500",
                )}
                style={{ width: `${Math.min(100, (used / current) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
      {children}
    </h3>
  );
}

function DLRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd
        className={cn(
          "text-sm",
          muted ? "italic text-slate-400" : "text-slate-700",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

const ROLE_BADGE_STYLE: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  Admin: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  Member: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  Viewer: { bg: "bg-slate-100", text: "text-slate-500", border: "border-slate-200" },
};

const DEFAULT_ROLE_BADGE = {
  bg: "bg-violet-50",
  text: "text-violet-700",
  border: "border-violet-200",
};

function RoleBadge({ roleName }: { roleName: string }) {
  const s = ROLE_BADGE_STYLE[roleName] ?? DEFAULT_ROLE_BADGE;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
        s.bg,
        s.text,
        s.border,
      )}
    >
      {roleName}
    </span>
  );
}

function ResetResultCard({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        `Email: ${email}\nPassword: ${password}`,
      );
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Select manually instead.");
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
      <dl className="space-y-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
            Email
          </dt>
          <dd className="truncate font-mono text-[13px] text-slate-900">
            {email}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
            Password
          </dt>
          <dd className="truncate font-mono text-[13px] font-semibold text-slate-900">
            {password}
          </dd>
        </div>
      </dl>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void copy()}
        className="w-full border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
      >
        {copied ? (
          <>
            <Check className="mr-2 h-3.5 w-3.5" />
            Copied
          </>
        ) : (
          <>
            <Copy className="mr-2 h-3.5 w-3.5" />
            Copy credentials
          </>
        )}
      </Button>
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-white/70 p-2.5 text-[11px] text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          This password will not be shown again. Send it to the member
          securely.
        </span>
      </div>
    </div>
  );
}

// Re-export `X` to keep it referenced; the icon is wired into the Sheet's
// built-in close button via Radix and doesn't need an explicit render here.
export { X as _UnusedX };

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return "An unexpected error occurred.";
}
